import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import RutaSearch from '../../../components/RutaSearch';
import { Ruta } from '../../../services/rutaService';
import clienteService, { ClienteExportItem } from '../../../services/clienteService';
import direccionClienteService from '../../../services/direccionClienteService';
import { CreateVisitasDto, VisitasDto } from '../../../types/visitas';
import { getApiErrorMessage } from '../../../utils/apiError';
import { SelectBox } from './planVisitaFormUi';

const MAX_RESULTADOS = 100;

interface AgregarClientesPlanDialogProps {
  open: boolean;
  visitasExistentes: VisitasDto[];
  saving?: boolean;
  onClose: () => void;
  onSubmit: (items: CreateVisitasDto[]) => Promise<void>;
}

const esClienteInactivo = (c: ClienteExportItem): boolean =>
  String(c.tipoCliente ?? '').trim().toUpperCase() === 'I';

const nombreCliente = (c: ClienteExportItem): string =>
  [c.nombre, c.razonSocial].filter((s) => s && String(s).trim()).join(' — ') ||
  `Cliente ${c.nroCliente}`;

/** Dirección de la visita: la de la zona elegida; si no, la única o la principal del cliente. */
const resolverCodigoDireccion = async (
  cliente: ClienteExportItem,
  codigoRuta?: number,
): Promise<string | undefined> => {
  const principal = cliente.codigoDireccion?.trim() || undefined;
  try {
    const direcciones = await direccionClienteService.getDirecciones(cliente.nroCliente);
    if (!direcciones?.length) return principal;
    const deZona = codigoRuta
      ? direcciones.find((d) => Number(d.codigoRuta) === codigoRuta)
      : undefined;
    if (deZona) return deZona.codigoDireccion;
    if (direcciones.length === 1) return direcciones[0].codigoDireccion;
    const coincidePrincipal = direcciones.find((d) => d.codigoDireccion === principal);
    return (coincidePrincipal ?? direcciones[0]).codigoDireccion;
  } catch {
    return principal;
  }
};

const AgregarClientesPlanDialog: React.FC<AgregarClientesPlanDialogProps> = ({
  open,
  visitasExistentes,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [clientes, setClientes] = useState<ClienteExportItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState('');
  const [ruta, setRuta] = useState<Ruta | null>(null);
  const [rutaSearchOpen, setRutaSearchOpen] = useState(false);
  const [seleccion, setSeleccion] = useState<number[]>([]);
  const [observaciones, setObservaciones] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!open) return;
    setFiltro('');
    setRuta(null);
    setSeleccion([]);
    setObservaciones({});
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open || clientes) return;
    let cancelado = false;
    setLoading(true);
    clienteService
      .getClientesExportData()
      .then((data) => {
        if (!cancelado) setClientes((data || []).filter((c) => !esClienteInactivo(c)));
      })
      .catch((err: unknown) => {
        if (!cancelado) setError(getApiErrorMessage(err, 'No se pudieron cargar los clientes.'));
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [open, clientes]);

  const clientesPorNro = useMemo(
    () => new Map((clientes ?? []).map((c) => [c.nroCliente, c])),
    [clientes],
  );

  const yaEnPlan = useMemo(
    () => new Set(visitasExistentes.map((v) => v.nroCliente)),
    [visitasExistentes],
  );

  const candidatos = useMemo(() => {
    if (!clientes) return [];
    const term = filtro.trim().toLowerCase();
    return clientes
      .filter((c) => !ruta?.codigoRuta || Number(c.codigoRuta) === ruta.codigoRuta)
      .filter(
        (c) =>
          !term ||
          nombreCliente(c).toLowerCase().includes(term) ||
          String(c.nroCliente).includes(term) ||
          String(c.direccion ?? '').toLowerCase().includes(term),
      )
      .sort((a, b) => nombreCliente(a).localeCompare(nombreCliente(b), 'es'));
  }, [clientes, filtro, ruta]);

  const candidatosVisibles = candidatos.slice(0, MAX_RESULTADOS);

  const agregar = (nroCliente: number) => {
    setSeleccion((prev) => (prev.includes(nroCliente) ? prev : [...prev, nroCliente]));
  };

  const quitar = (nroCliente: number) => {
    setSeleccion((prev) => prev.filter((n) => n !== nroCliente));
    setObservaciones((prev) => {
      const next = { ...prev };
      delete next[nroCliente];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (seleccion.length === 0) return;
    try {
      setPreparando(true);
      setError(null);
      const items: CreateVisitasDto[] = [];
      for (const nro of seleccion) {
        const cliente = clientesPorNro.get(nro);
        if (!cliente) continue;
        items.push({
          nroCliente: nro,
          codigoDireccion: await resolverCodigoDireccion(cliente, ruta?.codigoRuta),
          observaciones: observaciones[nro]?.trim() || null,
        });
      }
      setPreparando(false);
      await onSubmit(items);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'No se pudieron preparar las visitas.'));
      setPreparando(false);
    }
  };

  const ocupado = saving || preparando;

  return (
    <>
      <Dialog open={open} onClose={ocupado ? undefined : onClose} fullWidth maxWidth="lg">
        <DialogTitle>Agregar clientes al recorrido</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Buscá los clientes y agregalos en el orden en que se van a visitar. Podés anotar el
              pedido de cada uno. Las visitas se suman al final del recorrido; después se pueden
              reordenar desde el detalle del plan.
            </Typography>

            {error ? (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            ) : null}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">Buscar clientes</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder="Nombre, número o dirección"
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    disabled={ocupado}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <SelectBox
                        label="Zona (opcional)"
                        display={ruta ? `${ruta.codigoRuta} — ${ruta.nombreRuta?.trim() ?? ''}` : ''}
                        placeholder="Todas las zonas"
                        hideHelper
                        disabled={ocupado}
                        onOpen={() => setRutaSearchOpen(true)}
                      />
                    </Box>
                    {ruta ? (
                      <Tooltip title="Quitar filtro de zona">
                        <IconButton size="small" onClick={() => setRuta(null)} disabled={ocupado}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <TableContainer sx={{ maxHeight: 380 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Cliente</TableCell>
                              <TableCell>Dirección</TableCell>
                              <TableCell align="center" />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {candidatosVisibles.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3}>No hay clientes que coincidan.</TableCell>
                              </TableRow>
                            ) : (
                              candidatosVisibles.map((c) => {
                                const enPlan = yaEnPlan.has(c.nroCliente);
                                const elegido = seleccion.includes(c.nroCliente);
                                return (
                                  <TableRow key={c.nroCliente} hover>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {c.nroCliente} — {nombreCliente(c)}
                                      </Typography>
                                      {enPlan ? (
                                        <Chip label="Ya está en el plan" size="small" variant="outlined" />
                                      ) : null}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{c.direccion?.trim() || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title={elegido ? 'Ya agregado' : 'Agregar al recorrido'}>
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={enPlan || elegido || ocupado}
                                            onClick={() => agregar(c.nroCliente)}
                                          >
                                            <AddIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {candidatos.length > MAX_RESULTADOS ? (
                        <Typography variant="caption" color="text.secondary">
                          Mostrando {MAX_RESULTADOS} de {candidatos.length}. Refiná la búsqueda para
                          ver el resto.
                        </Typography>
                      ) : null}
                    </>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">
                    Recorrido a agregar ({seleccion.length})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 460 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Orden</TableCell>
                          <TableCell>Cliente</TableCell>
                          <TableCell sx={{ minWidth: 180 }}>Observaciones / pedido</TableCell>
                          <TableCell align="center" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {seleccion.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4}>
                              Todavía no agregaste clientes. Usá el botón + de la izquierda.
                            </TableCell>
                          </TableRow>
                        ) : (
                          seleccion.map((nro, idx) => {
                            const c = clientesPorNro.get(nro);
                            return (
                              <TableRow key={nro}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {nro} — {c ? nombreCliente(c) : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    disabled={ocupado}
                                    value={observaciones[nro] ?? ''}
                                    onChange={(e) =>
                                      setObservaciones((prev) => ({ ...prev, [nro]: e.target.value }))
                                    }
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Quitar">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={ocupado}
                                        onClick={() => quitar(nro)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={ocupado}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={seleccion.length === 0 || ocupado}
          >
            {preparando
              ? 'Preparando…'
              : saving
                ? 'Agregando…'
                : `Agregar ${seleccion.length || ''} visita${seleccion.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      <RutaSearch
        open={rutaSearchOpen}
        onClose={() => setRutaSearchOpen(false)}
        onSelect={(r) => setRuta(r)}
      />
    </>
  );
};

export default AgregarClientesPlanDialog;
